import BackToListing from '@components/BackToListing';
import CustomCard from '@components/CustomCard';
import useSwr from '@hooks/useSwr';
import { Button, Col, Descriptions, Row, Tooltip } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import CustomTag from '@components/CustomTag';
import { DollarOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import Groupings from '@pages/Administrative/Groupings';
import RoleChecker from '@components/RoleChecker';
import rolesConstants from '@utils/rolesConstants';
import AddCredit from '@components/AddCredit';
import { bytesFormat } from '@utils/formatters';
import { SharedPlanPayload } from '../NewSharedPlanModal';

interface SharedPlanProps extends SharedPlanPayload {
  id: number;
  department: {
    id: number;
    name: string;
  };
  currentConsumptionBytes: number;
  currentFranchiseBytes: number;
}

const SharedPlanDetails = () => {
  const { sharedPlanId } = useParams();
  const { t } = useTranslation(['groupings', 'attributes']);

  const [showAddCredit, setShowAddCredit] = useState(false);

  const { data } = useSwr<SharedPlanProps>(
    sharedPlanId ? `/service-proxy/broker/sharing-plans/${sharedPlanId}` : null,
  );

  const activatedTag = useCallback(
    (value: boolean | undefined) => (
      <CustomTag color={value ? 'green' : 'red'}>
        {t(
          `attributes:automaticBlocking.${value ? 'activated' : 'disabled'}`,
        ).toUpperCase()}
      </CustomTag>
    ),
    [t],
  );

  const renderConsumption = useMemo(() => {
    const consumption = data?.currentConsumptionBytes ?? 0;
    const franchise = data?.currentFranchiseBytes ?? 0;

    return (
      <div style={{ display: 'inline-flex', alignItems: 'center' }}>
        <CustomTag>
          {`${bytesFormat(consumption)} / ${
            franchise
              ? bytesFormat(franchise)
              : bytesFormat(data?.franchiseBytes)
          }`}
        </CustomTag>
        <Tooltip title={t('msgs.currentConsumptionInfo')}>
          <Button
            type="link"
            size="small"
            style={{ marginLeft: -6 }}
            icon={<QuestionCircleOutlined />}
          />
        </Tooltip>
      </div>
    );
  }, [
    data?.currentConsumptionBytes,
    data?.currentFranchiseBytes,
    data?.franchiseBytes,
    t,
  ]);

  const sharedPlanItemsDescriptions = useMemo(
    () => [
      {
        label: t('attributes:description'),
        value: data?.description,
        showItem: !!data?.description,
      },
      {
        label: `${t('attributes:consumption')} / ${t('attributes:franchise')}`,
        value: renderConsumption,
        showItem: true,
      },
      {
        label: t('attributes:automaticBlocking.title'),
        value: activatedTag(data?.automaticBlocking),
        showItem: true,
      },
      {
        label: t('attributes:department'),
        value: data?.department?.name,
        showItem: !!data?.department?.name,
      },
    ],
    [
      t,
      data?.description,
      data?.automaticBlocking,
      data?.department?.name,
      renderConsumption,
      activatedTag,
    ],
  );

  return (
    <Row gutter={[16, 16]}>
      {data && showAddCredit && (
        <RoleChecker role={rolesConstants.ADD_CREDIT_TO_SHARED_PLAN}>
          <AddCredit
            creditType="contract"
            idToAdd={data?.id}
            visible={showAddCredit}
            onClose={() => setShowAddCredit(false)}
          />
        </RoleChecker>
      )}
      <Col span={24}>
        <CustomCard
          title={
            <BackToListing
              style={{ margin: 0 }}
              title={
                <>
                  {t('sharedPlanDetails')}
                  <RoleChecker role={rolesConstants.ADD_CREDIT_TO_SHARED_PLAN}>
                    <Tooltip title={t('credits:add-credit')}>
                      <Button
                        icon={
                          <DollarOutlined
                            style={{ fontSize: '1.6rem', marginLeft: 8 }}
                          />
                        }
                        type="link"
                        onClick={() => setShowAddCredit(true)}
                      />
                    </Tooltip>
                  </RoleChecker>
                </>
              }
            />
          }
          loading={!data}
        >
          <Descriptions
            column={{ lg: 2, md: 2, sm: 2, xs: 1 }}
            style={{ marginTop: 24, padding: 16 }}
          >
            {sharedPlanItemsDescriptions?.map(
              ({ label, value, showItem }) =>
                value &&
                showItem && (
                  <Descriptions.Item key={label} label={label}>
                    {value}
                  </Descriptions.Item>
                ),
            )}
          </Descriptions>
        </CustomCard>
      </Col>
      {data && (
        <Col span={24}>
          <Groupings onlyTable sharingPlanId={data?.id} />
        </Col>
      )}
    </Row>
  );
};

export default SharedPlanDetails;
